import { useQuery } from '@tanstack/react-query';
import { getCveProfile } from '../../../api/risk/cve.api';
import type { CveMetadata, CveProfile } from '../types';

interface AxiosLikeError {
  response?: { status?: number };
}

function isNotFoundError(error: unknown): boolean {
  return (error as AxiosLikeError)?.response?.status === 404;
}

/**
 * Build a body-empty CveProfile from the citation metadata seed so the
 * panel header renders instantly while the rich payload is in flight.
 */
function seedProfile(seed: CveMetadata): CveProfile {
  return {
    cveId: seed.cveId,
    description: seed.description,
    epss: seed.epss,
    epssPercentile: seed.epssPercentile,
    cvss: seed.zdiMaxCvss,
    severity: undefined,
    kev: seed.kevListed
      ? {
          listed: true,
          dueDate: seed.kevDueDate,
          ransomwareCampaignUse: seed.kevRansomwareUse,
        }
      : null,
    attackerkbRisk: seed.attackerkbRisk,
    exploitCount: seed.exploitCount,
    verifiedExploitCount: seed.verifiedExploitCount,
    zerodayReported: seed.zerodayReported,
    cwes: [],
    affectedProducts: [],
    references: [],
  };
}

export interface UseCveProfileResult {
  profile: CveProfile | null;
  isLoading: boolean;
  isError: boolean;
  isNotFound: boolean;
  refetch: () => void;
}

export function useCveProfile(
  cveId: string | null,
  seed?: CveMetadata
): UseCveProfileResult {
  const query = useQuery<CveProfile>({
    queryKey: ['cve-profile', cveId],
    queryFn: () => getCveProfile(cveId as string).then((res) => res.data),
    enabled: !!cveId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const seeded = seed ? seedProfile(seed) : null;
  const profile = query.data ?? seeded;

  return {
    profile,
    isLoading: query.isLoading && !!cveId,
    isError: query.isError,
    isNotFound: query.isError && isNotFoundError(query.error),
    refetch: query.refetch,
  };
}
